import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// ─── Images & Videos ────────────────────────────────────────────────────────
import heroImage from '../assets/Mahi/60.jpeg';
import buddhistMonkImage from '../assets/Mahi/m.png';
import cookeryImage1 from '../assets/Mahi/92.jpeg';
import meditationImage1 from '../assets/Mahi/105.jpeg';
import nightBeachVideo from '../assets/Mahi/0223 (1).mp4';
import ayurvedaImage1 from '../assets/Mahi/3.jpeg';
import traditionalImage1 from '../assets/Mahi/119.jpeg';
import shanthiKarmaVideo from '../assets/Mahi/0223 (2).mp4';
import musicTherapyImage from '../assets/Mahi/142.jpeg';
import taiChiImage from '../assets/Mahi/143.jpeg';

// ─── Animation Variants ─────────────────────────────────────────────
const fadeUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: 'easeOut' } },
};

const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.9, ease: 'easeOut' } },
};

const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.9, ease: 'easeOut' } },
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.92 },
    visible: i => ({
        opacity: 1,
        scale: 1,
        transition: { delay: i * 0.08, duration: 0.7, ease: 'easeOut' },
    }),
};

const Events = () => {
    const { t } = useTranslation('events');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const events = [
        {
            key: 'buddhistMonkMeditation',
            title: t('buddhistMonkMeditationTitle'),
            subtitle: t('buddhistMonkMeditationSubtitle'),
            description: t('buddhistMonkMeditationDescription'),
            highlights: [
                t('buddhistMonkMeditationHighlight1'), t('buddhistMonkMeditationHighlight2'), t('buddhistMonkMeditationHighlight3'),
                t('buddhistMonkMeditationHighlight4'), t('buddhistMonkMeditationHighlight5'),
            ],
            image1: buddhistMonkImage,
            accent: '#C7A07C',
        },
        {
            key: 'cookery',
            title: t('cookeryTitle'),
            subtitle: t('cookerySubtitle'),
            description: t('cookeryDescription'),
            highlights: [
                t('cookeryHighlight1'), t('cookeryHighlight2'), t('cookeryHighlight3'),
                t('cookeryHighlight4'), t('cookeryHighlight5'),
            ],
            image1: cookeryImage1,
            accent: '#E4AB58',
        },
        {
            key: 'meditation',
            title: t('meditationTitle'),
            subtitle: t('meditationSubtitle'),
            description: t('meditationDescription'),
            highlights: [
                t('meditationHighlight1'), t('meditationHighlight2'), t('meditationHighlight3'),
                t('meditationHighlight4'), t('meditationHighlight5'),
            ],
            image1: meditationImage1,
            accent: '#C7A07C',
        },
        {
            key: 'nightBeachParties',
            title: t('nightBeachPartiesTitle'),
            subtitle: t('nightBeachPartiesSubtitle'),
            description: t('nightBeachPartiesDescription'),
            highlights: [
                t('nightBeachPartiesHighlight1'), t('nightBeachPartiesHighlight2'), t('nightBeachPartiesHighlight3'),
                t('nightBeachPartiesHighlight4'), t('nightBeachPartiesHighlight5'),
            ],
            video: nightBeachVideo,
            accent: '#E4AB58',
        },
        {
            key: 'ayurvedaLectures',
            title: t('ayurvedaLecturesTitle'),
            subtitle: t('ayurvedaLecturesSubtitle'),
            description: t('ayurvedaLecturesDescription'),
            highlights: [
                t('ayurvedaLecturesHighlight1'), t('ayurvedaLecturesHighlight2'), t('ayurvedaLecturesHighlight3'),
                t('ayurvedaLecturesHighlight4'), t('ayurvedaLecturesHighlight5'),
            ],
            image1: ayurvedaImage1,
            accent: '#C7A07C',
        },
        {
            key: 'traditionalCloths',
            title: t('traditionalClothsTitle'),
            subtitle: t('traditionalClothsSubtitle'),
            description: t('traditionalClothsDescription'),
            highlights: [
                t('traditionalClothsHighlight1'), t('traditionalClothsHighlight2'), t('traditionalClothsHighlight3'),
                t('traditionalClothsHighlight4'), t('traditionalClothsHighlight5'),
            ],
            image1: traditionalImage1,
            accent: '#E4AB58',
        },
        {
            key: 'shanthiKarma',
            title: t('shanthiKarmaTitle'),
            subtitle: t('shanthiKarmaSubtitle'),
            description: t('shanthiKarmaDescription'),
            highlights: [
                t('shanthiKarmaHighlight1'), t('shanthiKarmaHighlight2'), t('shanthiKarmaHighlight3'),
                t('shanthiKarmaHighlight4'), t('shanthiKarmaHighlight5'),
            ],
            video: shanthiKarmaVideo,
            accent: '#C7A07C',
        },
        {
            key: 'musicTherapy',
            title: t('musicTherapyTitle'),
            subtitle: t('musicTherapySubtitle'),
            description: t('musicTherapyDescription'),
            highlights: [
                t('musicTherapyHighlight1'), t('musicTherapyHighlight2'), t('musicTherapyHighlight3'),
                t('musicTherapyHighlight4'), t('musicTherapyHighlight5'),
            ],
            image1: musicTherapyImage,
            accent: '#E4AB58',
        },
        {
            key: 'taiChi',
            title: t('taiChiTitle'),
            subtitle: t('taiChiSubtitle'),
            description: t('taiChiDescription'),
            highlights: [
                t('taiChiHighlight1'), t('taiChiHighlight2'), t('taiChiHighlight3'),
                t('taiChiHighlight4'), t('taiChiHighlight5'),
            ],
            image1: taiChiImage,
            accent: '#C7A07C',
        },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-stone-50 via-[#f9f7f0] to-[#e8e4d6]">

            {/* ─── HERO ──────────────────────────────────────────────── */}
            <header className="relative h-[70vh] min-h-[520px] flex items-center justify-center overflow-hidden">
                <img
                    src={heroImage}
                    alt={t('heroAlt') || 'Mahi Beach Resort Events'}
                    className="absolute inset-0 w-full h-full object-cover brightness-[0.68] scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/45 to-black/60" />

                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={fadeUp}
                    className="relative z-10 text-center px-5 sm:px-8 max-w-5xl"
                >
                    <div className="inline-flex items-center gap-5 mb-6">
                        <div className="h-px w-16 bg-amber-400/60 flex-1" />
                        <span className="text-amber-300 uppercase tracking-[0.3em] text-sm md:text-base font-medium">
                            {t('heroEyebrow')}
                        </span>
                        <div className="h-px w-16 bg-amber-400/60 flex-1" />
                    </div>

                    <h1 className="
            text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl
            font-serif font-light tracking-tight text-white
            drop-shadow-[0_5px_18px_rgba(0,0,0,0.75)]
          ">
                        {t('heroTitle')}
                    </h1>

                    <p className="
            mt-6 sm:mt-8 text-lg sm:text-xl md:text-2xl lg:text-3xl
            text-amber-200/95 max-w-4xl mx-auto leading-relaxed
            drop-shadow-md
          ">
                        {t('heroSubtitle')}
                    </p>
                </motion.div>
            </header>

            {/* ─── EVENTS ─────────────────────────────────────────────── */}
            <main className="relative z-10 pb-20 md:pb-32 pt-12 md:pt-16">
                {events.map((event, index) => {
                    const isEven = index % 2 === 0;

                    return (
                        <section key={event.key} className="py-16 md:py-24 lg:py-32">
                            <div className="container mx-auto px-5 sm:px-8 lg:px-12">
                                <motion.div
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, margin: "-120px" }}
                                    variants={fadeUp}
                                    className="block md:hidden"
                                >
                                    <div className="text-center">
                                        <div className="inline-flex items-center gap-4 mb-4">
                                            <div className="h-px w-10 bg-gray-300/50 flex-1" />
                                            <span
                                                className="text-xs uppercase tracking-wider font-medium"
                                                style={{ color: event.accent }}
                                            >
                                                {event.subtitle}
                                            </span>
                                            <div className="h-px w-10 bg-gray-300/50 flex-1" />
                                        </div>

                                        <h2 className="text-3xl sm:text-4xl font-serif font-light text-[#1A2F2A] tracking-tight mb-5">
                                            {event.title}
                                        </h2>
                                    </div>

                                    <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl mb-6">
                                        {event.video ? (
                                            <video
                                                src={event.video}
                                                autoPlay
                                                muted
                                                loop
                                                playsInline
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <img
                                                src={event.image1}
                                                alt={event.title}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        )}
                                    </div>

                                    <p className="text-base sm:text-lg text-teal-800/85 leading-relaxed text-center">
                                        {event.description}
                                    </p>

                                    <div className="mt-8">
                                        <h3
                                            className="text-xl sm:text-2xl font-medium mb-5 text-center"
                                            style={{ color: event.accent }}
                                        >
                                            {t('highlightsTitle')}
                                        </h3>

                                        <ul className="grid gap-4">
                                            {event.highlights.map((highlight, i) => (
                                                <motion.li
                                                    key={i}
                                                    custom={i}
                                                    variants={scaleIn}
                                                    className="flex items-start gap-4 text-base text-teal-800/90"
                                                >
                                                    <span
                                                        className="w-3.5 h-3.5 rounded-full mt-1.5 flex-shrink-0"
                                                        style={{ backgroundColor: event.accent }}
                                                    />
                                                    {highlight}
                                                </motion.li>
                                            ))}
                                        </ul>
                                    </div>
                                </motion.div>

                                <motion.div
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, margin: "-120px" }}
                                    variants={fadeUp}
                                    className="hidden md:grid lg:grid-cols-2 gap-10 lg:gap-16 items-center"
                                >
                                    {/* Content Block */}
                                    <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                                        <div className="text-center lg:text-left">
                                            <div className="inline-flex items-center gap-4 mb-5">
                                                <div className="h-px w-12 bg-gray-300/50 flex-1 lg:hidden" />
                                                <span
                                                    className="text-sm md:text-base uppercase tracking-wider font-medium"
                                                    style={{ color: event.accent }}
                                                >
                                                    {event.subtitle}
                                                </span>
                                                <div className="h-px w-12 bg-gray-300/50 flex-1 lg:hidden" />
                                            </div>

                                            <h2 className="
                        text-3xl sm:text-4xl md:text-5xl lg:text-6xl
                        font-serif font-light text-[#1A2F2A] tracking-tight mb-6 md:mb-8
                      ">
                                                {event.title}
                                            </h2>

                                            <p className="text-lg md:text-xl text-teal-800/85 leading-relaxed max-w-3xl mx-auto lg:mx-0">
                                                {event.description}
                                            </p>
                                        </div>

                                        <div className="mt-10 md:mt-12">
                                            <h3
                                                className="text-2xl md:text-3xl font-medium mb-6"
                                                style={{ color: event.accent }}
                                            >
                                                {t('highlightsTitle')}
                                            </h3>

                                            <ul className="grid sm:grid-cols-2 gap-4 md:gap-6">
                                                {event.highlights.map((highlight, i) => (
                                                    <motion.li
                                                        key={i}
                                                        custom={i}
                                                        variants={scaleIn}
                                                        className="flex items-start gap-4 text-lg text-teal-800/90"
                                                    >
                                                        <span
                                                            className="w-3.5 h-3.5 rounded-full mt-1.5 flex-shrink-0"
                                                            style={{ backgroundColor: event.accent }}
                                                        />
                                                        {highlight}
                                                    </motion.li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    {/* Image or Video */}
                                    <motion.div
                                        variants={isEven ? fadeInRight : fadeInLeft}
                                        className={isEven ? 'lg:order-1' : 'lg:order-2'}
                                    >
                                        <div className="aspect-[4/3] rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl group">
                                            {event.video ? (
                                                <video
                                                    src={event.video}
                                                    autoPlay
                                                    muted
                                                    loop
                                                    playsInline
                                                    className="w-full h-full object-cover transition-transform duration-900 group-hover:scale-110"
                                                />
                                            ) : (
                                                <img
                                                    src={event.image1}
                                                    alt={event.title}
                                                    className="w-full h-full object-cover transition-transform duration-900 group-hover:scale-110"
                                                    loading="lazy"
                                                />
                                            )}
                                        </div>
                                    </motion.div>
                                </motion.div>
                            </div>
                        </section>
                    );
                })}
            </main>

            {/* ─── FINAL CTA ─────────────────────────────────────────────── */}
            <section className="py-20 md:py-28 bg-gradient-to-b from-[#e8e4d6] to-stone-100">
                <div className="container mx-auto px-5 sm:px-8 text-center">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUp}
                        className="
              bg-white/70 backdrop-blur-md rounded-3xl p-10 md:p-16 lg:p-20
              border border-teal-100/60 shadow-2xl
            "
                    >
                        <h2 className="
              text-3xl sm:text-4xl md:text-5xl lg:text-6xl
              font-serif font-light text-[#1A2F2A] mb-6 md:mb-8
            ">
                            {t('ctaTitle')}
                        </h2>

                        <p className="text-lg md:text-xl lg:text-2xl text-teal-800/85 max-w-3xl mx-auto mb-10 md:mb-12 leading-relaxed">
                            {t('ctaDescription')}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-5 md:gap-8 justify-center">
                            <a
                                href="/contact-us"
                                className="
                  inline-flex items-center justify-center gap-3
                  bg-gradient-to-r from-amber-500 to-amber-600
                  hover:from-amber-600 hover:to-amber-700
                  text-white font-medium text-lg md:text-xl
                  px-10 py-5 rounded-full shadow-xl hover:shadow-2xl
                  transform hover:-translate-y-1 transition-all duration-300
                "
                            >
                                {t('ctaContact')}
                                <span aria-hidden>→</span>
                            </a>

                            <a
                                href="/rooms"
                                className="
                  inline-flex items-center justify-center gap-3
                  border-2 border-teal-700 text-teal-800
                  hover:bg-teal-700 hover:text-white
                  font-medium text-lg md:text-xl
                  px-10 py-5 rounded-full transition-all duration-300
                "
                            >
                                {t('ctaViewRooms')}
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

        </div>
    );
};

export default Events;